// 自分のポインタ位置と表示ビューを viewer.sync として一定間隔で送信する hook。
// 30Hz だと API Gateway の課金が重いため基本 10Hz（100ms）に制限する。
// view（pan / zoom）はフォーカスモード追尾用に他クライアントへ即時中継される。

import { useEffect, useRef } from "react";
import { useRealtimeClient } from "../../realtime/realtime-context";
import { useCurrentActor, type CurrentActor } from "../../auth/use-current-actor";
import {
  CANVAS_HEIGHT_PX,
  CANVAS_WIDTH_PX,
  screenToStage,
} from "../../lib/canvas-coordinates";
import { useCanvasStore } from "./store/canvas-store";
import { useCanvasViewersStore } from "./store/canvas-viewers-store";
import type { RealtimeClient } from "../../realtime/realtime-client";
import type { Viewer } from "@refinement-canvas/domain";

const BROADCAST_INTERVAL_MS = 100;

/**
 * 参加人数に応じたカーソル送信間隔（ミリ秒）を返す。
 * カーソル同期はサーバが「送信者ごとに全参加者へ中継」するため、総配信数は
 * 参加人数 N に対して N ×送信レート×(N−1) ＝ N^2 に比例して増える。少人数では滑らかさを
 * 優先して 10Hz（100ms）のまま、多人数では送信間隔を広げて配信量の二乗成長を抑える
 * （20 人で総配信数を約 1/2、それ以上で約 1/3.5 に削減。カーソルは一時情報で、間隔が
 * 350ms でも追従表示としては実用上十分）。
 */
export function cursorBroadcastIntervalMs(viewerCount: number): number {
  if (viewerCount <= 8) return 100;
  if (viewerCount <= 20) return 200;
  return 350;
}
const VIEW_BROADCAST_INTERVAL_MS = 100;
// 静止中（pan/zoom していない）でも、後から参加した利用者がフォーカスモードで追尾を
// 開始できるよう、現在 view を低頻度で再送信するハートビート間隔。
// viewer.view は永続化されない一時情報のため、新規参加者は再送が無いと対象の view を取得できない。
const VIEW_HEARTBEAT_INTERVAL_MS = 2000;

export function useCursorBroadcaster(options?: { readonly readOnly?: boolean | undefined }): void {
  // 参照専用（埋め込み）ではプレゼンス（カーソル / view / 選択）を配信しない。
  // 送信自体は静的リアルタイムクライアントで no-op になるが、window の pointermove リスナーや
  // 定期タイマーを張らないよう、各 effect を readOnly で早期 return する。
  const readOnly = options?.readOnly ?? false;
  const client = useRealtimeClient();
  const actor = useCurrentActor();
  const actorRef = useRef(actor);
  actorRef.current = actor;
  const selectedObjectIds = useCanvasStore((s) => s.selectedObjectIds);
  const view = useCanvasStore((s) => s.view);
  const lastSentRef = useRef<number>(0);
  const pendingRef = useRef<{ x: number; y: number } | null>(null);
  // pan/zoom が変化したかどうかを追跡し、変化していない間は view を送らない。
  const lastSentViewRef = useRef<Viewer["view"] | null>(null);
  // 連続的な pan/zoom 中の view 送信を 100ms に間引くためのタイマー ID。
  const viewFlushTimerRef = useRef<number | undefined>(undefined);
  // 最新の view を ref 経由で参照する（pointermove ハンドラ内で常に最新を取れるようにする）。
  const viewRef = useRef(view);
  viewRef.current = view;
  // 遅延 flush 時に最新の選択状態を参照するための ref。
  // タイマー作成時点の closure に固定された古い selectedObjectIds で上書きするのを防ぐ。
  const selectedObjectIdsRef = useRef(selectedObjectIds);
  selectedObjectIdsRef.current = selectedObjectIds;

  useEffect(() => {
    if (readOnly) return;
    // 初期表示と選択変更は viewer.sync（カーソル・view なし）として共有する。
    // view を載せるとサーバ側が viewer.snapshot を配信しなくなり、参加者一覧や他者選択枠が
    // 更新されなくなるため、メタデータ同期は必ず view なしで送って snapshot を発火させる。
    // 初期 view は下の throttle effect が初回マウント時に別途送信する。
    sendViewerSync(client, actor, selectedObjectIds);
  }, [actor, client, readOnly, selectedObjectIds]);

  // 再接続時に presence を再登録する。サーバは cursor / view なしの viewer.sync でのみ
  // viewer.snapshot を broadcast するため、再接続後に plain viewer.sync を送り直して
  // 自分を参加者一覧へ復帰させる（cursor / view ハートビートだけでは snapshot が出ない）。
  useEffect(() => {
    if (readOnly) return undefined;
    return client.onReopen(() => {
      sendViewerSync(client, actor, selectedObjectIdsRef.current);
    });
  }, [actor, client, readOnly]);

  // view 変更を 100ms 単位にスロットリングして送信する。
  // 直近送信から 100ms 以上経過していれば即時、未経過なら残り時間後にまとめて送信する。
  // 初回マウント時は lastSentViewRef が null のため、現在 view を 1 度だけ送信する。
  useEffect(() => {
    if (readOnly) return undefined;
    const last = lastSentViewRef.current;
    const sameAsLast =
      !!last &&
      Math.abs(last.panX - view.panX) < 0.5 &&
      Math.abs(last.panY - view.panY) < 0.5 &&
      Math.abs(last.zoom - view.zoom) < 1e-4;
    if (sameAsLast) return undefined;

    const flush = (): void => {
      viewFlushTimerRef.current = undefined;
      const currentView = viewRef.current;
      // 色と選択状態は最新値を ref から読み、古い遅延同期で上書きしない。
      sendViewerSync(client, actorRef.current, selectedObjectIdsRef.current, undefined, currentView);
      lastSentViewRef.current = {
        panX: currentView.panX,
        panY: currentView.panY,
        zoom: currentView.zoom,
      };
    };

    if (viewFlushTimerRef.current !== undefined) {
      // 既存タイマー中（短時間に view が再度変わったケース）はタイマーの満了を待つ。
      return undefined;
    }
    viewFlushTimerRef.current = window.setTimeout(flush, VIEW_BROADCAST_INTERVAL_MS);
    return () => {
      // 後続の useEffect で view が再変更されたとき、タイマーは保ったままで次の flush を待つ。
      // unmount 時のみクリアする。
    };
    // selectedObjectIds は ref 経由で参照するため依存に含めない（view 変化時のみ flush を予約する）。
  }, [actor, client, readOnly, view]);

  // unmount で残タイマーをクリーンアップ。
  useEffect(() => {
    return () => {
      if (viewFlushTimerRef.current !== undefined) {
        window.clearTimeout(viewFlushTimerRef.current);
        viewFlushTimerRef.current = undefined;
      }
    };
  }, []);

  // 静止中の view ハートビート。
  // pan/zoom が止まると上の throttle effect は発火しないため、フォーカス追尾の元データが
  // 新規参加者へ届かなくなる。低頻度（2 秒間隔）で現在 view を再送して追尾開始を可能にする。
  useEffect(() => {
    if (readOnly) return undefined;
    const id = window.setInterval(() => {
      const currentView = viewRef.current;
      sendViewerSync(client, actor, selectedObjectIdsRef.current, undefined, currentView);
      lastSentViewRef.current = {
        panX: currentView.panX,
        panY: currentView.panY,
        zoom: currentView.zoom,
      };
    }, VIEW_HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [actor, client, readOnly]);

  useEffect(() => {
    if (readOnly) return undefined;
    const onPointerMove = (event: PointerEvent): void => {
      const currentView = viewRef.current;
      const stagePoint = screenToStage({ x: event.clientX, y: event.clientY }, currentView);
      // 当初仕様（キャンバス外にも配置可）へ戻したため、比率を [0,1] へ丸めない。丸めると外周・大サイズや
      // キャンバス外のカーソルが端に張り付いて見える。基準寸法は全クライアント共通の固定値のため、受信側は
      // 比率×基準寸法で元のステージ座標へ厳密に復元でき、範囲外でも正しい位置に描画される。
      const xRatio = stagePoint.x / CANVAS_WIDTH_PX;
      const yRatio = stagePoint.y / CANVAS_HEIGHT_PX;
      pendingRef.current = { x: xRatio, y: yRatio };
      flushIfDue();
    };

    const flushIfDue = (): void => {
      const now = Date.now();
      // 送信間隔は参加人数で適応制御する（多人数時の O(N^2) 配信量の抑制）。
      // 人数は viewers store から都度読む（購読による再レンダーを避けるため getState を使う）。
      const viewerCount = Object.keys(useCanvasViewersStore.getState().viewers).length;
      if (now - lastSentRef.current < cursorBroadcastIntervalMs(viewerCount)) return;
      const pending = pendingRef.current;
      if (!pending) return;
      lastSentRef.current = now;
      pendingRef.current = null;
      sendViewerSync(client, actor, selectedObjectIds, {
        xRatio: pending.x,
        yRatio: pending.y,
      });
    };

    window.addEventListener("pointermove", onPointerMove);
    const intervalId = window.setInterval(flushIfDue, BROADCAST_INTERVAL_MS);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.clearInterval(intervalId);
    };
  }, [actor, client, readOnly, selectedObjectIds]);
}

function sendViewerSync(
  client: RealtimeClient,
  actor: CurrentActor,
  selectedObjectIds: readonly string[],
  cursor?: Viewer["cursor"],
  view?: Viewer["view"]
): void {
  client.sendMessage({
    type: "viewer.sync",
    viewer: {
      userId: actor.userId,
      displayName: actor.displayName,
      color: actor.color,
      avatarImageUrl: actor.avatarImageUrl,
      selectedObjectIds,
      ...(cursor ? { cursor } : {}),
      ...(view ? { view } : {}),
    },
  });
}
